// Browser-side auth adapter for the PWA. Uses MSAL.js (auth code + PKCE via popup).
// Same interface as src/auth/interactive.mjs so exporter/graph modules don't care
// which runtime they're in.
//
// Peer-imports the msal-browser SDK — the PWA bundler pulls it in at build time.
import * as msal from '@azure/msal-browser';

export function createBrowserAuth({
  clientId,
  tenantId,
  scopes,
  redirectUri = window.location.origin + '/',
  // Called just before we show a Microsoft popup (fresh sign-in OR mid-flow
  // re-auth). Awaited, so the UI can show a banner and let the user click
  // "OK" — a user gesture is what keeps browsers from blocking the popup.
  onInteractiveRequired = null,
} = {}) {
  if (!clientId) throw new Error('clientId required');
  if (!tenantId) throw new Error('tenantId required');
  if (!scopes?.length) throw new Error('scopes required');

  const pca = new msal.PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });

  let initialized = false;
  async function ensureInit() {
    if (initialized) return;
    await pca.initialize();
    await pca.handleRedirectPromise().catch(() => null);
    initialized = true;
  }

  function getActiveAccount() {
    const accts = pca.getAllAccounts();
    return accts[0] || null;
  }

  async function acquireInteractive({ reason = 'sign-in' } = {}) {
    if (onInteractiveRequired) {
      // UI can throw here to cancel (e.g., user clicked "not now").
      await onInteractiveRequired({ reason });
    }
    const result = await pca.acquireTokenPopup({
      scopes,
      prompt: reason === 'expired' ? 'none' : 'select_account',
    });
    if (!result?.accessToken) throw new Error('sign-in returned no token');
    pca.setActiveAccount(result.account);
    return result.accessToken;
  }

  async function getToken({ forceRefresh = false } = {}) {
    await ensureInit();
    const account = getActiveAccount();
    if (account) {
      try {
        const result = await pca.acquireTokenSilent({ account, scopes, forceRefresh });
        if (result?.accessToken) return result.accessToken;
      } catch (err) {
        if (err instanceof msal.InteractionRequiredAuthError) {
          return acquireInteractive({ reason: 'expired' });
        }
        // Network hiccup / transient — one more silent try isn't going to help;
        // fall through to interactive as a last resort.
      }
    }
    return acquireInteractive({ reason: account ? 'expired' : 'sign-in' });
  }

  async function signIn() {
    await ensureInit();
    return acquireInteractive();
  }

  async function signOut() {
    await ensureInit();
    const account = getActiveAccount();
    if (account) {
      // logoutPopup redirects Microsoft's page. Silent local wipe is enough for our purposes.
      await pca.clearCache();
    }
  }

  async function whoAmI() {
    await ensureInit();
    return getActiveAccount();
  }

  return { getToken, signIn, signOut, whoAmI };
}
